import type { SuggestionPreset } from '../../TeoApi';

const DESCRIPTION_OPTIMIZE_SENTENCE = 'Wähle den **Satz** im Text aus, den du optimieren möchtest.';
const DESCRIPTION_OPTIMIZE_PARAGRAPH =
  'Wähle den **Absatz** im Text aus, den du optimieren möchtest.';

// Presets that only differ in what they work on say the same thing while they work.
const LOADING_MORE_PERSONAL = 'Text persönlicher formulieren...';
const LOADING_ACTIVATE_MORE = 'Stärker auffordern...';
const LOADING_ACTIVATE_LESS = 'Weniger auffordern...';
const LOADING_SHORTEN_SENTENCES = 'Sätze kürzen...';
const LOADING_ABSTRACT_NOUNS = 'Abstrakte Substantive ersetzen...';
const LOADING_NOUNS = 'Substantive umformulieren...';
const LOADING_SHORTEN_WORDS = 'Lange Wörter ersetzen...';

/** Shown for a preset that names no message of its own. */
export const DEFAULT_LOADING_MESSAGE = 'KI verbessert den Text...';
export type MarkerType =
  | 'paragraph'
  | 'sentence'
  | 'passive_sentence'
  | 'verb_noun'
  | 'abstract_noun'
  | 'simple_noun'
  | 'long_word'
  | 'medium_long_word'
  | 'long_sentence';

export interface AiSuggestionPreset {
  id: string;
  type: SuggestionPreset;
  allowOtherMarkers: boolean;
  markers: MarkerType[];
  contextMarker?: ContextMarkerType;
  dimensions: Dimension[];
  title: string;
  description: string;
  /** Shown under the loading animation while the AI works with this preset. */
  loadingMessage: string;
}

const presets = [
  {
    id: 'suggestions_vi',
    type: 'suggestions_vi',
    markers: ['paragraph'],
    allowOtherMarkers: true,
    title: 'Verständlichkeit (Absatz)',
    description: DESCRIPTION_OPTIMIZE_PARAGRAPH,
    loadingMessage: 'Verständlichkeit verbessern...',
    dimensions: [],
  },
  {
    id: 'suggestions_ai',
    type: 'suggestions_ai',
    markers: ['paragraph'],
    allowOtherMarkers: true,
    title: 'Abstraktheit (Absatz)',
    description: DESCRIPTION_OPTIMIZE_PARAGRAPH,
    loadingMessage: 'Text konkreter formulieren...',
    dimensions: [],
  },
  {
    id: 'suggestions_hi',
    type: 'suggestions_hi',
    markers: ['paragraph'],
    allowOtherMarkers: true,
    title: 'Persönlicher (Absatz)',
    description: DESCRIPTION_OPTIMIZE_PARAGRAPH,
    loadingMessage: LOADING_MORE_PERSONAL,
    dimensions: ['personal'],
  },
  {
    id: 'suggestions_hi_sentence',
    type: 'suggestions_hi',
    markers: ['sentence'],
    allowOtherMarkers: true,
    title: 'Persönlicher (Satz)',
    description: DESCRIPTION_OPTIMIZE_SENTENCE,
    loadingMessage: LOADING_MORE_PERSONAL,
    dimensions: ['personal'],
  },
  {
    id: 'suggestions_ii_low',
    type: 'suggestions_ii_low',
    markers: ['paragraph'],
    allowOtherMarkers: true,
    title: 'Stärker auffordern (Absatz)',
    description: DESCRIPTION_OPTIMIZE_PARAGRAPH,
    loadingMessage: LOADING_ACTIVATE_MORE,
    dimensions: ['activation'],
  },
  {
    id: 'suggestions_ii_low_sentence',
    type: 'suggestions_ii_low',
    markers: ['sentence'],
    allowOtherMarkers: true,
    title: 'Stärker auffordern (Satz)',
    description: DESCRIPTION_OPTIMIZE_SENTENCE,
    loadingMessage: LOADING_ACTIVATE_MORE,
    dimensions: ['activation'],
  },
  {
    id: 'suggestions_ii_high',
    type: 'suggestions_ii_high',
    markers: ['paragraph'],
    allowOtherMarkers: true,
    title: 'Weniger auffordern (Absatz)',
    description: DESCRIPTION_OPTIMIZE_PARAGRAPH,
    loadingMessage: LOADING_ACTIVATE_LESS,
    dimensions: ['activation'],
  },
  {
    id: 'suggestions_ii_high_sentence',
    type: 'suggestions_ii_high',
    markers: ['sentence'],
    allowOtherMarkers: true,
    title: 'Weniger auffordern (Satz)',
    description: DESCRIPTION_OPTIMIZE_SENTENCE,
    loadingMessage: LOADING_ACTIVATE_LESS,
    dimensions: ['activation'],
  },
  {
    id: 'suggestions_lsentence_paragraph',
    type: 'suggestions_lsentence',
    markers: ['paragraph'],
    allowOtherMarkers: true,
    title: 'Sätze kürzen (Absatz)',
    description: DESCRIPTION_OPTIMIZE_PARAGRAPH,
    loadingMessage: LOADING_SHORTEN_SENTENCES,
    dimensions: ['comprehensibility'],
  },
  {
    id: 'suggestions_lsentence',
    type: 'suggestions_lsentence',
    markers: ['sentence'],
    allowOtherMarkers: true,
    title: 'Sätze kürzen (Satz)',
    description: DESCRIPTION_OPTIMIZE_SENTENCE,
    loadingMessage: LOADING_SHORTEN_SENTENCES,
    dimensions: ['comprehensibility'],
  },
  {
    id: 'suggestions_abstract_paragraph',
    type: 'suggestions_abstract',
    markers: ['paragraph'],
    allowOtherMarkers: true,
    title: 'Abstrakte Substantive (Absatz)',
    description: DESCRIPTION_OPTIMIZE_PARAGRAPH,
    loadingMessage: LOADING_ABSTRACT_NOUNS,
    dimensions: ['abstractness'],
  },
  {
    id: 'suggestions_abstract_sentence',
    type: 'suggestions_abstract',
    markers: ['sentence'],
    allowOtherMarkers: true,
    title: 'Abstrakte Substantive (Satz)',
    description: DESCRIPTION_OPTIMIZE_SENTENCE,
    loadingMessage: LOADING_ABSTRACT_NOUNS,
    dimensions: ['abstractness'],
  },
  {
    id: 'suggestions_abstract',
    type: 'suggestions_abstract',
    markers: ['abstract_noun'],
    contextMarker: 'sentence',
    allowOtherMarkers: false,
    title: 'Abstrakte Substantive (Wort)',
    description: 'Wähle das **abstrakte Substantiv** im Text aus, das du ersetzen möchtest.',
    loadingMessage: LOADING_ABSTRACT_NOUNS,
    dimensions: ['abstractness'],
  },
  {
    id: 'suggestions_noun_paragraph',
    type: 'suggestions_noun',
    markers: ['paragraph'],
    allowOtherMarkers: true,
    title: 'Substantive (Absatz)',
    description: DESCRIPTION_OPTIMIZE_PARAGRAPH,
    loadingMessage: LOADING_NOUNS,
    dimensions: ['abstractness'],
  },
  {
    id: 'suggestions_noun_sentence',
    type: 'suggestions_noun',
    markers: ['sentence'],
    allowOtherMarkers: true,
    title: 'Substantive (Satz)',
    description: DESCRIPTION_OPTIMIZE_SENTENCE,
    loadingMessage: LOADING_NOUNS,
    dimensions: ['abstractness'],
  },
  {
    id: 'suggestions_noun',
    type: 'suggestions_noun',
    markers: ['simple_noun', 'verb_noun', 'abstract_noun'],
    contextMarker: 'sentence',
    allowOtherMarkers: false,
    title: 'Substantive (Wort)',
    description: 'Wähle das **Substantiv** im Text aus, das du ersetzen möchtest.',
    loadingMessage: LOADING_NOUNS,
    dimensions: ['abstractness'],
  },
  {
    id: 'suggestions_passive',
    type: 'suggestions_passive',
    markers: ['passive_sentence'],
    allowOtherMarkers: false,
    title: 'Satz',
    description: 'Wähle den **passiven Satz** im Text aus, den du optimieren möchtest.',
    loadingMessage: 'Passive Sätze umformulieren...',
    dimensions: ['passive'],
  },
  {
    id: 'suggestions_long_word_paragraph',
    type: 'suggestions_long_word',
    markers: ['paragraph'],
    allowOtherMarkers: true,
    title: 'Wörter kürzen (Absatz)',
    description: DESCRIPTION_OPTIMIZE_PARAGRAPH,
    loadingMessage: LOADING_SHORTEN_WORDS,
    dimensions: ['comprehensibility'],
  },
  // {
  //   id: 'suggestions_substantiations',
  //   type: 'suggestions_substantiations',
  //   markers: ['verb_noun'],
  //   contextMarker: 'sentence',
  //   allowOtherMarkers: false,
  //   title: 'Substantivierungen',
  //   description: 'Wähle die **Substantivierung** im Text aus, die du ersetzen möchtest.',
  //   dimensions: [],
  // },
  {
    id: 'suggestions_long_word',
    type: 'suggestions_long_word',
    markers: ['long_word', 'medium_long_word'],
    contextMarker: 'sentence',
    allowOtherMarkers: false,
    title: 'Wörter kürzen (Wort)',
    dimensions: ['comprehensibility'],
    description: 'Wähle das **lange Wort** im Text aus, das du ersetzen möchtest.',
    loadingMessage: LOADING_SHORTEN_WORDS,
  },
  {
    id: 'translate_plain_language',
    type: 'translate_plain_language',
    markers: [],
    allowOtherMarkers: true,
    title: 'Einfache Sprache',
    description: '',
    loadingMessage: 'In Einfache Sprache übertragen...',
    dimensions: [],
  },
  {
    id: 'rewrite_tpi',
    type: 'rewrite_tpi',
    markers: [],
    allowOtherMarkers: true,
    title: 'Text neu schreiben',
    description: '',
    loadingMessage: 'Text neu schreiben...',
    dimensions: [],
  },
] as const satisfies AiSuggestionPreset[];

export type AISuggestionPrompt = Omit<(typeof presets)[number], 'available'> & {
  available: boolean;
  contextMarker?: ContextMarkerType; // contextMarker is not extracted from typeof presets???
};
export type AISuggestionPromptId = AISuggestionPrompt['id'];
export type AISuggestionPromptType = AISuggestionPrompt['type'];

export const suggestionPresets: Map<AISuggestionPromptId, AISuggestionPrompt> = new Map(
  presets.map((preset) => [preset.id, { ...preset, available: true }]),
);
export function getAISuggestionPrompt(id: AISuggestionPromptId): AISuggestionPrompt {
  const prompt = suggestionPresets.get(id);
  if (!prompt) {
    throw new Error(`Unbekannter Prompt-ID: ${id}`);
  }
  return structuredClone(prompt);
}

/**
 * The message to show while the AI works with a preset.
 *
 * Presets of the same type share their message, because it names what the AI does and not
 * what it works on, so the first one of the type answers for all of them.
 */
export function getPresetLoadingMessage(type: SuggestionPreset): string {
  return presets.find((preset) => preset.type === type)?.loadingMessage ?? DEFAULT_LOADING_MESSAGE;
}

export function getSuggestionPromptsByDimension(dimension?: string): AISuggestionPrompt[] {
  if (!dimension) {
    return [];
  }
  return Array.from(
    suggestionPresets
      .values()
      .filter(
        // @ts-expect-error - this will be fixed, once all dimensions are set
        (preset) => 'dimensions' in preset && preset.dimensions.includes(dimension as Dimension),
      )
      .map((preset) => ({
        ...preset,
        available: true,
      })),
  );
}

type ContextMarkerType = 'sentence' | 'paragraph';
type Dimension =
  | 'comprehensibility'
  | 'passive'
  | 'personal'
  | 'activation'
  | 'abstractness'
  | 'comprehensibility';
